import { useState } from 'react';
import {
  Box,
  Button,
  ButtonGroup,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { toast } from 'react-toastify';
import {
  useOpenerState,
  useResetGroupDefaultConfig,
  useSaveGroupDefaultConfig
} from '../../api/useEntry';
import type { OpenerGroup } from '../../api/useGroups';
import {
  useOpenerGroupPanic,
  useOpenerGroupPanicSoft,
  useOpenerGroupStart,
  useOpenerGroupStop,
  useOpenerGroupsPodState
} from '../../api/useOpenerGroupPods';
import { FinishGroupButton } from './FinishGroupButton';
import {
  GROUP_POD_STATUS_CHIP_COLORS,
  GROUP_POD_STATUS_LABELS,
  canPanicGroup,
  canStartGroup,
  canStopGroup,
  deriveGroupPodStatus
} from './groupPodStatus';

type ConfirmAction = 'stop' | 'panic-soft' | 'panic' | 'reset-default';

/**
 * Per-group pod controls — rendered on the Entry page ONLY while a
 * specific group is selected. Start/Stop drive the group's dedicated
 * engine + feed pods via prediction-manager; Soft Panic cancels + pauses
 * the group's rules (engine pod stays); Hard Panic kills the group engine
 * pod and cancels ALL the group's orders externally. Mirrors the
 * StopStartOpenerButtons conventions (compact chip + outlined buttons,
 * warning = soft, error = hard).
 */
export const GroupPodControls = ({
  groupId,
  groupName,
  group
}: {
  groupId: string;
  groupName?: string | null;
  /** Active group object — supplies `defaultConfigSavedAt`/`pairCount` for the
   *  Save/Reset default controls. Optional so callers without it still render
   *  the pod controls. */
  group?: Pick<OpenerGroup, 'defaultConfigSavedAt' | 'pairCount'> | null;
}) => {
  const { data: podState } = useOpenerGroupsPodState();
  const { data: openerState } = useOpenerState();
  const startMutation = useOpenerGroupStart();
  const stopMutation = useOpenerGroupStop();
  const panicSoftMutation = useOpenerGroupPanicSoft();
  const panicMutation = useOpenerGroupPanic();
  const saveDefaultMutation = useSaveGroupDefaultConfig();
  const resetDefaultMutation = useResetGroupDefaultConfig();

  const groupState = podState?.find(g => g.groupId === groupId);
  const status = deriveGroupPodStatus(groupState);
  const lockout = openerState?.panicLockout;
  const lockoutArmed = Boolean(lockout?.armed);
  const isBusy =
    startMutation.isLoading ||
    stopMutation.isLoading ||
    panicSoftMutation.isLoading ||
    panicMutation.isLoading ||
    saveDefaultMutation.isLoading ||
    resetDefaultMutation.isLoading;

  const [confirm, setConfirm] = useState<ConfirmAction | null>(null);
  const [startMenuAnchor, setStartMenuAnchor] = useState<null | HTMLElement>(
    null
  );

  const label = groupName || groupState?.name || 'this group';

  const startDisabled = isBusy || !canStartGroup(status, lockoutArmed);

  const defaultSavedAt = group?.defaultConfigSavedAt ?? null;
  const hasDefault = Boolean(defaultSavedAt);

  const handleSaveDefault = () => {
    saveDefaultMutation.mutate(groupId, {
      onSuccess: result => {
        toast(
          `Saved default config for "${label}" (${result.ruleCount} rule${
            result.ruleCount === 1 ? '' : 's'
          }).`,
          { theme: 'dark', type: 'success', autoClose: 4000 }
        );
      }
    });
  };

  const confirmCopy: Record<
    ConfirmAction,
    { title: string; body: string; cta: string; color: 'warning' | 'error' }
  > = {
    stop: {
      title: `Stop group — ${label}`,
      body:
        `Stops the pods of "${label}": pauses the group's rules, cancels ` +
        'their resting orders during the engine drain, then removes the ' +
        "group's engine + feed pods. Other groups and the residual opener " +
        'are NOT touched. No lockout — the group can be started again.',
      cta: 'Stop group',
      color: 'warning'
    },
    'panic-soft': {
      title: `Soft panic — ${label}`,
      body:
        `Cancels all open orders of "${label}" and pauses its rules ` +
        '(group-scoped PANIC 24H). The group engine pod STAYS up. Other ' +
        'groups are NOT touched. No lockout.',
      cta: 'Soft panic group',
      color: 'warning'
    },
    panic: {
      title: `HARD panic — ${label}`,
      body:
        `KILLS the engine pod of "${label}" immediately and cancels ALL ` +
        "the group's orders externally on Kalshi + Polymarket, then pauses " +
        'its rules. Other groups are NOT touched. No lockout — the group ' +
        'can be restarted right away.',
      cta: 'Hard panic group',
      color: 'error'
    },
    'reset-default': {
      title: `Reset to default — ${label}`,
      body:
        `Restores each rule in "${label}" to its OWN saved config (applied ` +
        'live to running rules; rules added after the last Save are left ' +
        'untouched). CONFIG ONLY — this will NOT enable or disable anything ' +
        '(maker flags and platform on/off are untouched).',
      cta: 'Reset to default',
      color: 'warning'
    }
  };

  const runConfirmed = () => {
    if (confirm === 'stop') stopMutation.mutate(groupId);
    else if (confirm === 'panic-soft') panicSoftMutation.mutate(groupId);
    else if (confirm === 'panic') panicMutation.mutate(groupId);
    else if (confirm === 'reset-default') resetDefaultMutation.mutate(groupId);
    setConfirm(null);
  };

  return (
    <>
      <Stack direction="row" spacing={0.5} alignItems="center">
        <Tooltip
          title={
            status === 'provisioning'
              ? 'Group pods desired ON — waiting for the engine pod to become Ready'
              : status === 'running'
                ? `Group engine + feed pods running${
                    typeof groupState?.engineReplicas === 'number'
                      ? ` (${groupState.engineReplicas} engine replica${
                          groupState.engineReplicas === 1 ? '' : 's'
                        })`
                      : ''
                  }`
                : status === 'panicked'
                  ? 'Group hard-panicked — engine pod killed, orders cancelled. Start brings it back.'
                  : 'Group pods are off — rules in this group run nowhere until started'
          }
          placement="top"
        >
          <Chip
            size="small"
            label={`Pods: ${GROUP_POD_STATUS_LABELS[status]}`}
            color={GROUP_POD_STATUS_CHIP_COLORS[status]}
            variant={status === 'off' ? 'outlined' : 'filled'}
            icon={
              status === 'provisioning' ? (
                <CircularProgress size={10} color="inherit" sx={{ ml: 0.5 }} />
              ) : undefined
            }
            sx={{ fontSize: '0.65rem', height: 20, mr: 0.25 }}
          />
        </Tooltip>
        <Tooltip
          title={
            lockoutArmed
              ? `Global opener panic lockout armed${
                  lockout?.expiresAt
                    ? ` until ${new Date(lockout.expiresAt).toLocaleString()}`
                    : ''
                } — group start is refused while it holds`
              : status === 'running' || status === 'provisioning'
                ? 'Group pods already on'
                : `Start dedicated engine + feed pods for "${label}"`
          }
          placement="top"
        >
          <span>
            <ButtonGroup variant="outlined" color="success" size="small">
              <Button
                startIcon={
                  startMutation.isLoading ? (
                    <CircularProgress size={12} color="inherit" />
                  ) : (
                    <PlayArrowIcon />
                  )
                }
                onClick={() =>
                  startMutation.mutate({ groupId, testAccount: false })
                }
                disabled={startDisabled}
                sx={{ fontSize: '0.7rem' }}
              >
                Start group
              </Button>
              <Button
                size="small"
                disabled={startDisabled}
                onClick={e => setStartMenuAnchor(e.currentTarget)}
                sx={{ px: 0.25, minWidth: 0 }}
                aria-label="Start options"
              >
                <ArrowDropDownIcon fontSize="small" />
              </Button>
            </ButtonGroup>
          </span>
        </Tooltip>
        <Menu
          anchorEl={startMenuAnchor}
          open={Boolean(startMenuAnchor)}
          onClose={() => setStartMenuAnchor(null)}
        >
          <MenuItem
            onClick={() => {
              startMutation.mutate({ groupId, testAccount: true });
              setStartMenuAnchor(null);
            }}
            sx={{ alignItems: 'flex-start', maxWidth: 280 }}
          >
            <Box>
              <Typography
                sx={{ fontSize: '0.8rem', color: 'warning.main', fontWeight: 600 }}
              >
                Start in test mode
              </Typography>
              <Typography sx={{ fontSize: '0.7rem', color: 'grey.500' }}>
                Trades against the separate low-balance TEST account.
              </Typography>
            </Box>
          </MenuItem>
        </Menu>
        <Tooltip
          title={
            status === 'off'
              ? 'Group pods already off'
              : `Stop "${label}" — pause its rules + drain and remove its pods`
          }
          placement="top"
        >
          <span>
            <Button
              variant="outlined"
              color="warning"
              size="small"
              startIcon={
                stopMutation.isLoading ? (
                  <CircularProgress size={12} color="inherit" />
                ) : (
                  <PowerSettingsNewIcon />
                )
              }
              onClick={() => setConfirm('stop')}
              disabled={isBusy || !canStopGroup(status)}
              sx={{ fontSize: '0.7rem' }}
            >
              Stop group
            </Button>
          </span>
        </Tooltip>
        <Tooltip
          title={`Cancel "${label}"'s open orders + pause its rules. Engine pod stays up.`}
          placement="top"
        >
          <span>
            <Button
              variant="contained"
              color="warning"
              size="small"
              startIcon={
                panicSoftMutation.isLoading ? (
                  <CircularProgress size={12} color="inherit" />
                ) : (
                  <WarningAmberIcon />
                )
              }
              onClick={() => setConfirm('panic-soft')}
              disabled={isBusy || !canPanicGroup(status)}
              sx={{ fontSize: '0.7rem' }}
            >
              Soft panic
            </Button>
          </span>
        </Tooltip>
        <Tooltip
          title={`Kill "${label}"'s engine pod + cancel ALL its orders externally.`}
          placement="top"
        >
          <span>
            <Button
              variant="contained"
              color="error"
              size="small"
              startIcon={
                panicMutation.isLoading ? (
                  <CircularProgress size={12} color="inherit" />
                ) : (
                  <WarningAmberIcon />
                )
              }
              onClick={() => setConfirm('panic')}
              disabled={isBusy || !canPanicGroup(status)}
              sx={{ fontSize: '0.7rem' }}
            >
              Hard panic
            </Button>
          </span>
        </Tooltip>
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
        <FinishGroupButton groupId={groupId} groupName={label} />
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
        <Tooltip
          title={
            hasDefault && defaultSavedAt
              ? `Snapshot each rule's current config as its own default (overwrites the one saved ${new Date(
                  defaultSavedAt
                ).toLocaleString()})`
              : "Snapshot each rule's current config as its own default"
          }
          placement="top"
        >
          <span>
            <Button
              variant="outlined"
              color="info"
              size="small"
              startIcon={
                saveDefaultMutation.isLoading ? (
                  <CircularProgress size={12} color="inherit" />
                ) : (
                  <SaveOutlinedIcon />
                )
              }
              onClick={handleSaveDefault}
              disabled={isBusy}
              sx={{ fontSize: '0.7rem' }}
            >
              Save default
            </Button>
          </span>
        </Tooltip>
        <Tooltip
          title={
            hasDefault
              ? `Restore each rule in "${label}" to its own saved config (config only)`
              : 'No default saved yet — press "Save default" first'
          }
          placement="top"
        >
          <span>
            <Button
              variant="outlined"
              color="warning"
              size="small"
              startIcon={
                resetDefaultMutation.isLoading ? (
                  <CircularProgress size={12} color="inherit" />
                ) : (
                  <RestartAltIcon />
                )
              }
              onClick={() => setConfirm('reset-default')}
              disabled={isBusy || !hasDefault}
              sx={{ fontSize: '0.7rem' }}
            >
              Reset to default
            </Button>
          </span>
        </Tooltip>
      </Stack>

      <Dialog
        open={confirm !== null}
        onClose={() => setConfirm(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: '1rem' }}>
          {confirm ? confirmCopy[confirm].title : ''}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '0.85rem' }}>
            {confirm ? confirmCopy[confirm].body : ''}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button size="small" onClick={() => setConfirm(null)}>
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            color={confirm ? confirmCopy[confirm].color : 'warning'}
            onClick={runConfirmed}
          >
            {confirm ? confirmCopy[confirm].cta : ''}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
